import axios, { AxiosResponse } from 'axios'
import {
  SingleResourceResponse,
  MultiResourceResponse,
  Identity,
  ResourceTypes,
  MultiResourceIdResponse,
  ResourceId,
  Organization,
  UpdateIdentity,
} from '@vendorflow/types'

import { AZIMUTH_API_URL, VERSION } from 'constants/Api.constants'
import { convertResourceToEntity } from '@vendorflow/common/lib/converters/JsonApi.converter'
import { AUTHORIZATION_SENTINEL } from 'features/auth/Auth.constants'
import { ORGANIZATION_URL } from 'apis/Organizations.api'
import qs from 'qs'
const IDENTITIES_URL = `${AZIMUTH_API_URL}/${VERSION}/identities`

/**
 * Identity CRUD
 */
export async function fetchIdentityUsingGET(id: string): Promise<Identity> {
  const result: AxiosResponse<SingleResourceResponse<Identity, ResourceTypes.IDENTITIES>> = await axios.get(
    `${IDENTITIES_URL}/${id}`,
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )

  return convertResourceToEntity(result.data.data)
}

export async function fetchIdentitiesUsingGET(): Promise<Identity[]> {
  const result: AxiosResponse<MultiResourceResponse<Identity, ResourceTypes.IDENTITIES>> = await axios.get(
    IDENTITIES_URL,
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )

  return result.data.data.map((item) => convertResourceToEntity(item))
}

export async function updateIdentityUsingPUT(updatedIdentity: UpdateIdentity): Promise<Identity> {
  const { id, type, ...attributes } = updatedIdentity
  const result: AxiosResponse<SingleResourceResponse<Identity, ResourceTypes.IDENTITIES>> = await axios.request({
    method: 'PUT',
    url: `${IDENTITIES_URL}/${updatedIdentity.id}`,
    data: { data: { attributes, id, type } },
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })

  return convertResourceToEntity(result.data.data)
}

export async function deleteIdentityUsingDELETE(id: string): Promise<Identity> {
  const result: AxiosResponse<SingleResourceResponse<Identity, ResourceTypes.IDENTITIES>> = await axios.delete(
    `${IDENTITIES_URL}/${id}`,
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )

  return convertResourceToEntity(result.data.data)
}

/**
 * Identity's Watched Resources
 */
export async function fetchWatchedByIdentityUsingGET(id: string): Promise<ResourceId<ResourceTypes>[]> {
  const result: AxiosResponse<MultiResourceIdResponse<ResourceTypes>> = await axios.request({
    method: 'GET',
    url: `${IDENTITIES_URL}/${id}/rel/watching`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })

  return result.data.data
}

export async function addToWatchedByIdentityUsingPOST(id: string, resourceIds: ResourceId<ResourceTypes>[]) {
  await axios.request({
    method: 'POST',
    url: `${IDENTITIES_URL}/${id}/rel/watching`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
    data: { data: resourceIds },
  })
}

export async function removeFromWatchedByIdentityUsingDELETE(id: string, resourceIds: ResourceId<ResourceTypes>[]) {
  await axios.request({
    method: 'DELETE',
    url: `${IDENTITIES_URL}/${id}/rel/watching`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
    data: { data: resourceIds },
  })
}

/**
 * Contact Sharing Between Envies
 */
export async function fetchAliceManagedByUsingGET(alice: string): Promise<Organization[]> {
  const result: AxiosResponse<MultiResourceResponse<Organization, ResourceTypes.ORGANIZATIONS>> = await axios.request({
    method: 'GET',
    url: `${IDENTITIES_URL}/${alice}/rel/managedBy`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })

  return result.data.data.map((item) => convertResourceToEntity(item))
}

export async function addAliceManagedByUsingPOST(
  alice: string,
  enviesToAdd: ResourceId<ResourceTypes.ORGANIZATIONS>[]
) {
  await axios.request({
    method: 'POST',
    url: `${IDENTITIES_URL}/${alice}/rel/managedBy`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
    data: { data: enviesToAdd },
  })
}

export async function removeAliceManagedByUsingDELETE(
  alice: string,
  enviesToRemove: ResourceId<ResourceTypes.ORGANIZATIONS>[]
) {
  await axios.request({
    method: 'DELETE',
    url: `${IDENTITIES_URL}/${alice}/rel/managedBy`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
    data: { data: enviesToRemove },
  })
}

export async function fetchBradBelongsToUsingGET(brad: string): Promise<Organization[]> {
  const result: AxiosResponse<MultiResourceResponse<Organization, ResourceTypes.ORGANIZATIONS>> = await axios.request({
    method: 'GET',
    url: `${IDENTITIES_URL}/${brad}/rel/memberOf`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })

  return result.data.data.map((item) => convertResourceToEntity(item))
}

export async function fetchManagedIdentitiesInRootUsingGET(
  id: string
): Promise<AxiosResponse<MultiResourceResponse<Identity, ResourceTypes.IDENTITIES>>> {
  return await axios.request({
    method: 'GET',
    url: `${ORGANIZATION_URL}/${id}/managedIdentities?${qs.stringify({
      scope: 'organizations.exportsManagedIdentities',
    })}`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })
}
